
// InvQuote.js - Invoice/Quote create/edit form (web, adapted from React Native)
import React, { useState, useEffect } from 'react';
import { Form, Input, DatePicker, Select, Button, Table, Space, Typography, message } from 'antd';
import CustomerSelect from './CustomerSelect';
import AddItem from './AddItem';
import RecordPayment from './RecordPayment';
import { v4 as uuidv4 } from 'uuid';
import dayjs from 'dayjs';
import jsPDF from 'jspdf';
import { baseUrl_SPB } from '../../BaseUrl';
import { useAuth } from '../../AuthProvider';

const { Title } = Typography;
const { TextArea } = Input;

const taxOptions = [
  { label: 'Tax Exclusive', value: 'exclusive' },
  { label: 'Tax Inclusive', value: 'inclusive' },
];

const formatMoney = (value) => {
  const numericValue = Number(value || 0);
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
  }).format(Number.isNaN(numericValue) ? 0 : numericValue);
};

const formatDocumentDate = (value) => {
  if (!value) {
    return '';
  }

  const dateValue = dayjs.isDayjs(value) ? value : dayjs(value);
  if (!dateValue.isValid()) {
    return String(value);
  }

  return dateValue.format('DD MMM YYYY');
};

const toDataUrl = async (imageUrl) => {
  if (!imageUrl) {
    return null;
  }

  const response = await fetch(imageUrl);
  const blob = await response.blob();

  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

const InvQuote = ({ id,type = 'invoice', item, onSave, onCancel }) => {
  const [form] = Form.useForm();
  const [items, setItems] = useState(item?.items || []);
  const [payments, setPayments] = useState(item?.payments || []);
  const [addItemVisible, setAddItemVisible] = useState(false);
  const [recordPaymentVisible, setRecordPaymentVisible] = useState(false);
  const [paidChecked, setPaidChecked] = useState(item?.paid === 'Y');
  // Multi-action state
  const [saveAs, setSaveAs] = useState(item?.saveAs || '');
  const [paymentLocked, setPaymentLocked] = useState(item?.paymentLocked || false);

  // Generate invoice/quote number with prefix
  const generateNumber = (type) => {
    const prefix = type === 'quote' ? 'Q' : 'I';
    const uniqueId = uuidv4(); // Use the first part of the UUID
    return `${prefix}-${uniqueId}`;
  };
  const auth = useAuth();
  const processItem = (item) => {
      // Parse date fields if they are in DD/MM/YYYY format
      let dated = item.dated;
      let dueDate = item.dueDate;
      // If string and matches DD/MM/YYYY, convert to dayjs
      const parseDMY = (str) => {
        if (typeof str === 'string' && str.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
          const [d, m, y] = str.split('/').map(Number);
          return dayjs(`${y}-${m.toString().padStart(2, '0')}-${d.toString().padStart(2, '0')}`);
        }
        return dayjs(str);
      };
      if (dated) dated = parseDMY(dated);
      if (dueDate) dueDate = parseDMY(dueDate);
      form.setFieldsValue({ ...item, dated, dueDate });
       setItems(item.items || item.invOrQuoteItems || []);
      setPayments(item.payments || item.invPaymentItems || []);
      setPaidChecked(item.paid === 'Y');
  }
  useEffect(() => {
    console.log('InvQuote item', item);
    if (item) {
      // Parse date fields if they are in DD/MM/YYYY format
      let dated = item.dated;
      let dueDate = item.dueDate;
      // If string and matches DD/MM/YYYY, convert to dayjs
      const parseDMY = (str) => {
        if (typeof str === 'string' && str.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
          const [d, m, y] = str.split('/').map(Number);
          return dayjs(`${y}-${m.toString().padStart(2, '0')}-${d.toString().padStart(2, '0')}`);
        }
        return dayjs(str);
      };
      if (dated) dated = parseDMY(dated);
      if (dueDate) dueDate = parseDMY(dueDate);
      form.setFieldsValue({ ...item, dated, dueDate });
      setItems(item.items || item.invOrQuoteItems || []);
      setPayments(item.payments || item.invPaymentItems || []);
      setPaidChecked(item.paid === 'Y');
    }
  }, [item, form]);
useEffect(() => {
  // 1. Define your async data fetch function
  const fetchData = async () => {
    try {
      const response = await fetch(`${baseUrl_SPB}/viewiq/getsingleinvquote?id=` + id, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      
      // 2. Do something with the data right here inside the async scope!
     processItem(data);
    } catch (error) {
      console.error("Error fetching invoice/quote details:", error);
    }
  };
  
  // 3. Fire off the function if a valid ID exists
  if (id) {
    fetchData();
  }
}, [id, form]); // Added form to dependencies if it comes from an external hook (like Form.useForm())
// When adding an item, do NOT apply GST to the item's total. GST is only applied at the invoice/quote summary level.
  const handleAddItem = (item) => {
    // Ensure item.total is just price * quantity, no GST
    const price = parseFloat(item.price) || 0;
    const quantity = parseFloat(item.rquantity || item.quantity) || 1;
    const total = price * quantity;
    setItems([...items, { ...item, total: total.toFixed(2), key: uuidv4() }]);
    setAddItemVisible(false);
  };

  const handleAddPayment = (payment) => {
    setPayments([...payments, { ...payment, key: uuidv4() }]);
    setRecordPaymentVisible(false);
  };

  const calculateTotal = () => {
    return items.reduce((sum, item) => sum + parseFloat(item.total), 0);
  };

const calculateGST = (taxOption) => {
  const total = calculateTotal();

  if (taxOption === 'inclusive') {
    return total / 11;
  }

  // exclusive case (GST not included in price)
  return total * 0.10;
};
 const calculateTotalWithGST = (taxOption) => {
  const total = calculateTotal();

  if (taxOption === 'inclusive') {
    return total;
  }

  // exclusive: add GST on top of net total
  return total * 1.10;
};

  const calculateTotalPaid = () => {
    return payments.reduce((sum, p) => sum + parseFloat(p.amount), 0);
  };
  const getCompanyAddress = () => {
    const company = auth.additionalUserInfo?.company || {};
    if (company.companyAddress) {
      return company.companyAddress;
    }

    const locations = Array.isArray(company.locations) ? company.locations : [];
    const preferredLocation = locations.find((location) => location.defaultLocation === 'Y') || locations[0];
    if (!preferredLocation) {
      return '';
    }

    return [
      preferredLocation.streetNo,
      preferredLocation.streetName,
      preferredLocation.suburb,
      preferredLocation.city,
      preferredLocation.state,
      preferredLocation.postCode,
      preferredLocation.country,
    ].filter(Boolean).join(', ');
  };

  const buildDownloadDocument = () => {
    const values = form.getFieldsValue(true);
    const taxOption = values.taxOption || item?.taxOption || 'exclusive';
    const totalExGST = calculateAmountExclGST(taxOption);
    const totalGST = calculateGST(taxOption);
    const grandTotal = calculateTotalWithGST(taxOption);
    const totalPaid = calculateTotalPaid();
    const company = auth.additionalUserInfo?.company || {};
    const customer = values.customerObj || item?.customerObj || {};

    return {
      documentLabel: type === 'quote' ? 'Quote' : 'Tax Invoice',
      actionLabel: type === 'quote' ? 'Download Quote' : 'Download Invoice',
      number: values.invoiceOrQuoteNumber || item?.invoiceOrQuoteNumber || generateNumber(type),
      issueDate: values.dated || item?.dated || dayjs(),
      dueDate: values.dueDate || item?.dueDate || dayjs().add(7, 'day'),
      companyName: company.companyName || auth.additionalUserInfo?.companyName || '',
      companyABN: company.companyABN || auth.additionalUserInfo?.companyABN || '',
      companyAddress: getCompanyAddress(),
      companyPhones: Array.isArray(company.companyPhoneNumbers) ? company.companyPhoneNumbers.filter(Boolean) : [],
      companyEmail: auth.additionalUserInfo?.email || auth.additionalUserInfo?.userName || '',
      brandImageUrl: company.brandImageUrl || auth.additionalUserInfo?.brandImageUrl || '',
      bankName: company.bankDetails?.[0]?.bankName || '',
      bsbNumber: company.bankDetails?.[0]?.bsbNumber || '',
      accountNumber: company.bankDetails?.[0]?.accountNumber || '',
      customerName: customer.label || values.customerName || item?.customerName || '',
      customerEmail: values.customerEmail || item?.customerEmail || '',
      customerPhone: values.customerPhone || item?.customerPhone || '',
      items: items.map((entry) => ({
        name: entry.name || entry.itemName || 'Line item',
        quantity: Number(entry.quantity || entry.rquantity || 0),
        price: Number(entry.price || entry.unitPrice || 0),
        total: Number(entry.total || 0),
      })),
      taxOption,
      totalExGST,
      totalGST,
      grandTotal,
      totalPaid,
      amountDue: grandTotal - totalPaid,
      payments: payments.map((payment) => ({
        date: payment.date,
        amount: Number(payment.amount || 0),
        reference: payment.reference || '',
      })),
    };
  };

  const handleDownloadDocument = async () => {
    try {
      const documentData = buildDownloadDocument();
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const leftMargin = 14;
      const rightMargin = pageWidth - 14;
      let cursorY = 18;

      const drawTextRow = (label, value, options = {}) => {
        if (!value) {
          return;
        }

        const labelWidth = options.labelWidth || 36;
        pdf.setFont('helvetica', 'bold');
        pdf.text(label, leftMargin, cursorY);
        pdf.setFont('helvetica', 'normal');
        const lines = pdf.splitTextToSize(String(value), rightMargin - leftMargin - labelWidth);
        pdf.text(lines, leftMargin + labelWidth, cursorY);
        cursorY += lines.length * 5 + 1;
      };

      const ensurePageSpace = (requiredHeight = 16) => {
        if (cursorY + requiredHeight <= pageHeight - 16) {
          return;
        }

        pdf.addPage();
        cursorY = 18;
      };

      const logoDataUrl = documentData.brandImageUrl ? await toDataUrl(documentData.brandImageUrl).catch(() => null) : null;
      if (logoDataUrl) {
        try {
          pdf.addImage(logoDataUrl, 'PNG', leftMargin, cursorY - 2, 24, 24);
        } catch (error) {
          try {
            pdf.addImage(logoDataUrl, 'JPEG', leftMargin, cursorY - 2, 24, 24);
          } catch (imageError) {
            console.error('Unable to render brand image in PDF:', imageError);
          }
        }
      }

      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(20);
      pdf.text(documentData.documentLabel.toUpperCase(), rightMargin, cursorY + 2, { align: 'right' });
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Document No: ${documentData.number}`, rightMargin, cursorY + 10, { align: 'right' });
      pdf.text(`Issue Date: ${formatDocumentDate(documentData.issueDate)}`, rightMargin, cursorY + 16, { align: 'right' });
      pdf.text(`${type === 'quote' ? 'Valid Until' : 'Due Date'}: ${formatDocumentDate(documentData.dueDate)}`, rightMargin, cursorY + 22, { align: 'right' });
      cursorY += 34;

      pdf.setDrawColor(223, 228, 234);
      pdf.line(leftMargin, cursorY, rightMargin, cursorY);
      cursorY += 8;

      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(11);
      pdf.text('Issued By', leftMargin, cursorY);
      pdf.text(type === 'quote' ? 'Prepared For' : 'Bill To', 110, cursorY);
      cursorY += 6;
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);

      const companyLines = [
        documentData.companyName,
        documentData.companyABN ? `ABN ${documentData.companyABN}` : '',
        documentData.companyAddress,
        documentData.companyPhones.join(' | '),
        documentData.companyEmail,
      ].filter(Boolean);
      const customerLines = [
        documentData.customerName,
        documentData.customerEmail,
        documentData.customerPhone,
      ].filter(Boolean);

      const renderedCompanyLines = pdf.splitTextToSize(companyLines.join('\n'), 82);
      const renderedCustomerLines = pdf.splitTextToSize(customerLines.join('\n'), 82);
      pdf.text(renderedCompanyLines, leftMargin, cursorY);
      pdf.text(renderedCustomerLines, 110, cursorY);
      cursorY += Math.max(renderedCompanyLines.length, renderedCustomerLines.length) * 5 + 8;

      ensurePageSpace(18);
      pdf.setFillColor(22, 34, 53);
      pdf.rect(leftMargin, cursorY, rightMargin - leftMargin, 9, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Description', leftMargin + 2, cursorY + 6);
      pdf.text('Qty', 110, cursorY + 6);
      pdf.text('Unit', 130, cursorY + 6);
      pdf.text('Amount', rightMargin - 2, cursorY + 6, { align: 'right' });
      pdf.setTextColor(0, 0, 0);
      cursorY += 12;

      pdf.setFont('helvetica', 'normal');
      documentData.items.forEach((lineItem, index) => {
        ensurePageSpace(12);
        const descriptionLines = pdf.splitTextToSize(lineItem.name, 84);
        const rowHeight = Math.max(8, descriptionLines.length * 5);
        pdf.text(descriptionLines, leftMargin + 2, cursorY);
        pdf.text(String(lineItem.quantity || 0), 110, cursorY);
        pdf.text(formatMoney(lineItem.price), 130, cursorY);
        pdf.text(formatMoney(lineItem.total), rightMargin - 2, cursorY, { align: 'right' });
        pdf.setDrawColor(236, 240, 241);
        pdf.line(leftMargin, cursorY + rowHeight, rightMargin, cursorY + rowHeight);
        cursorY += rowHeight + 4;
      });

      ensurePageSpace(42);
      cursorY += 4;
      const summaryXLabel = 128;
      const summaryXValue = rightMargin - 2;
      const writeSummaryLine = (label, value, bold = false) => {
        pdf.setFont('helvetica', bold ? 'bold' : 'normal');
        pdf.text(label, summaryXLabel, cursorY);
        pdf.text(formatMoney(value), summaryXValue, cursorY, { align: 'right' });
        cursorY += 6;
      };

      writeSummaryLine('Subtotal (ex GST)', documentData.totalExGST);
      writeSummaryLine('GST (10%)', documentData.totalGST);
      writeSummaryLine('Total (inc GST)', documentData.grandTotal, true);
      if (type !== 'quote') {
        writeSummaryLine('Paid to Date', documentData.totalPaid);
        writeSummaryLine('Balance Due', documentData.amountDue, true);
      }

      ensurePageSpace(38);
      cursorY += 6;
      pdf.setFont('helvetica', 'bold');
      pdf.text(type === 'quote' ? 'Acceptance & Next Steps' : 'Payment Details', leftMargin, cursorY);
      cursorY += 6;
      pdf.setFont('helvetica', 'normal');
      const footerLines = type === 'quote'
        ? [
            'This quote is prepared in Australian dollars and includes GST as shown above.',
            `Quote valid until ${formatDocumentDate(documentData.dueDate)} unless otherwise agreed in writing.`,
            'To proceed, please approve the quote and confirm your preferred scheduling window.',
          ]
        : [
            documentData.companyName,
            documentData.bankName,
            documentData.bsbNumber ? `BSB: ${documentData.bsbNumber}` : '',
            documentData.accountNumber ? `Account: ${documentData.accountNumber}` : '',
            `Please reference ${documentData.number} when making payment.`,
          ].filter(Boolean);
      const footerText = pdf.splitTextToSize(footerLines.join('\n'), rightMargin - leftMargin);
      pdf.text(footerText, leftMargin, cursorY);

      pdf.save(`${type === 'quote' ? 'Quote' : 'Invoice'}_${documentData.number}.pdf`);
    } catch (error) {
      console.error('Failed to generate invoice/quote PDF:', error);
      message.error(`Unable to download this ${type === 'quote' ? 'quote' : 'invoice'} right now.`);
    }
  };
const calculateAmountExclGST = (taxOption) => {
  const total = calculateTotal();

  if (taxOption === 'inclusive') {
    const gst = total / 11;
    return total - gst;
  }

  // exclusive means already net
  return total;
};

  const processPayment = () => {

  if (!item.activeStripeCheckoutUrl) {
    alert("Payment link not available. Please refresh invoice.");
    return;
  }

  if (item.stripePaymentStatus === "PAID") {
    alert("Invoice already paid.");
    return;
  }

  window.open(item.activeStripeCheckoutUrl, "_blank");
};
  const sendEmailReminder = async (isReminder) => {
    const url = `${baseUrl_SPB}/api/v1/invorquote/saveandemail`;
    const token = auth.token;
    setSaveAs(saveAs);

     const payload = {
      ...item,
      id: item.id || null,
      invoiceOrQuoteNumber: item.invoiceOrQuoteNumber,
      customerId: item.customerId,
      customerEmail: item.customerEmail || item.customerEmail || '',
      customerName: item.customerName,
      customerPhone: item.customerPhone || '',
      dueDate: item.dueDate,
      dated: item.dated,
      description: item.description || '',
      taxOption: item.taxOption,
      invOrQuoteItems: item.items || item.invOrQuoteItems || [],
      invPaymentItems: item.payments || item.invPaymentItems || [],
      grandTotal: item.total,
      totalGST: item.totalGST,
      type: type,
      paymentRecords: item.payments || item.invPaymentItems || [],
      isReminder: isReminder,
    };
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      const responseData = await response.json();
     
    } catch (error) {
      console.log('Error', 'An error occurred while sending email to client');
    }
  };

  // Multi-action save handler
  const handleSave = (values, saveAsValue = saveAs) => {
    const customer = values.customerObj;
    const gstValue = calculateGST(values.taxOption).toFixed(2);
    const result = {
      ...values,
      id: item.id || null,
      customerId: customer?.value,
      customerName: customer?.label,
      items,
      payments,
      type,
      total: calculateTotalWithGST(values.taxOption).toFixed(2),
      grandTotal: calculateTotalWithGST(values.taxOption).toFixed(2),
      amountExclGST: calculateAmountExclGST(values.taxOption).toFixed(2),
      totalGST: gstValue,
      invoiceOrQuoteNumber: values.invoiceOrQuoteNumber || generateNumber(type),
      dated: values.dated ? values.dated.format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
      dueDate: values.dueDate ? values.dueDate.format('YYYY-MM-DD') : dayjs().add(7, 'day').format('YYYY-MM-DD'),
      description: values.description || '',
      paid: paidChecked ? 'Y' : '',
      saveAs: saveAsValue,
    };
    onSave(result, type);
  };

  // Form submit handler (default is Save)
  const onFinish = (values) => {
    handleSave(values, saveAs);
  };

  return (
    <div>
      <Title level={4}>{item ? 'View/Edit' : 'Create'} {type === 'invoice' ? 'Invoice' : 'Quote'}</Title>
      <Form form={form} layout="vertical" onFinish={onFinish} initialValues={{ taxOption: 'exclusive' }}>
        <Form.Item label="Customer" name="customerObj" rules={[{ required: true, message: 'Please select a customer' }]}> 
          <CustomerSelect />
        </Form.Item>
        <Form.Item label="Invoice/Quote Number" name="invoiceOrQuoteNumber"> 
          <Input disabled={!!item} />
        </Form.Item>
        <Form.Item label="Dated" name="dated" rules={[{ required: true, message: 'Please select a date' }]}> 
          <DatePicker style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item label="Due Date" name="dueDate" rules={[{ required: true, message: 'Please select a due date' }]}> 
          <DatePicker style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item label="Tax Option" name="taxOption"> 
          <Select options={taxOptions} style={{ width: 200 }} disabled />
        </Form.Item>
        <Form.Item label="Summary/Description" name="description"> 
          <TextArea rows={4} />
        </Form.Item>
        <div style={{ margin: '16px 0' }}>
          <Space>
            <span style={{ fontWeight: 500 }}>Items</span>
            <Button onClick={() => setAddItemVisible(true)}>Add Item</Button>
          </Space>
          <Table
            dataSource={items}
            columns={[
              { title: 'Product', dataIndex: 'name', key: 'name' },
              { title: 'Price', dataIndex: 'price', key: 'price', render: v => `$${v}` },
              { title: 'Quantity', dataIndex: 'quantity', key: 'quantity' },
              { title: 'Total', dataIndex: 'total', key: 'total', render: v => `$${v}` },
              {
                title: 'Action',
                key: 'action',
                render: (_, record) => (
                  <Button danger size="small" onClick={() => setItems(items.filter(i => i.key !== record.key))}>
                    Delete
                  </Button>
                ),
              },
            ]}
            pagination={false}
            rowKey="key"
          />
          <Space style={{ marginTop: 16, float: 'right' }} direction="vertical">
            <span>GST on Income: ${calculateGST(form.getFieldValue('taxOption')).toFixed(2)}</span>
            <span><b>Total: ${calculateTotalWithGST(form.getFieldValue('taxOption')).toFixed(2)}</b></span>
          </Space>
        </div>
        <div style={{ margin: '16px 0' }}>
          <Space>
            <span style={{ fontWeight: 500 }}>Payments</span>
            <Button onClick={() => setRecordPaymentVisible(true)}>Record Payment</Button>
            <span style={{ marginLeft: 16 }}>
              <input
                type="checkbox"
                checked={paidChecked}
                disabled={item?.paid === 'Y'}
                onChange={e => {
                  const checked = e.target.checked;
                  setPaidChecked(checked);
                  if (checked) {
                    // Add payment record if not already present for grand total
                    const total = calculateTotalWithGST(form.getFieldValue('taxOption'));
                    const today = dayjs().format('YYYY-MM-DD');
                    // Only add if not already present
                    const alreadyExists = payments.some(p => parseFloat(p.amount) === parseFloat(total) && p.date === today);
                    if (!alreadyExists) {
                      setPayments([...payments, { date: today, amount: total.toFixed(2), reference: 'Auto (Paid)', key: uuidv4() }]);
                    }
                  }
                }}
                style={{ marginRight: 8 }}
              />
              <span>Paid (Payment received)</span>
            </span>
          </Space>
          <Table
            dataSource={payments}
            columns={[
              { title: 'Date', dataIndex: 'date', key: 'date' },
              { title: 'Amount', dataIndex: 'amount', key: 'amount', render: v => `$${v}` },
              { title: 'Notes', dataIndex: 'reference', key: 'reference' },
            ]}
            pagination={false}
            rowKey="key"
          />
          <Space style={{ marginTop: 16, float: 'right' }} direction="vertical">
            <span>Total Paid: ${calculateTotalPaid().toFixed(2)}</span>
            <span><b>Total Due: ${(calculateTotalWithGST(form.getFieldValue('taxOption')) - calculateTotalPaid()).toFixed(2)}</b></span>
          </Space>
        </div>
        <Form.Item>
          <Space>
            <Button onClick={onCancel}>Cancel</Button>
            <Button onClick={handleDownloadDocument}>{type === 'quote' ? 'Download Quote' : 'Download Invoice'}</Button>
            {/* Send Reminder button if saveAs === 'due' */}
            {saveAs === 'due' ? (
              <Button onClick={() => sendEmailReminder('Y')}>Send Reminder</Button>
            ) : saveAs !== '' && saveAs !== null && saveAs !== undefined  && paidChecked === 'Y' ? (
              <Button onClick={() => sendEmailReminder("N")}>Email</Button>
            ) : null}

            {/* Approve button if saveAs === 'draft' */}
            {saveAs === 'draft' ? (
              <Button onClick={() => { setSaveAs('due'); form.submit(); }}>Approve</Button>
            ) : null}

            {/* Save as Draft or Save button depending on saveAs */}
            {(saveAs === '' || saveAs === null || saveAs === undefined) ? (
              <Button type="primary" onClick={() => { setSaveAs('draft'); form.submit(); }}>Save as Draft</Button>
            ) : (
              <Button type="primary" onClick={() => form.submit()}>Save</Button>
            )}

             {/* Approve button if saveAs === 'draft' */}
            {saveAs === 'due' && paymentLocked!='Y' && !paidChecked ? (
              <Button onClick={processPayment}>Process Payment-----{paymentLocked}</Button>
            ) : null}

          </Space>
        </Form.Item>
      </Form>
      <AddItem
        visible={addItemVisible}
        onCancel={() => setAddItemVisible(false)}
        onAddItem={handleAddItem}
        taxOption={form.getFieldValue('taxOption')}
      />
      <RecordPayment
        visible={recordPaymentVisible}
        onClose={() => setRecordPaymentVisible(false)}
        onSave={handleAddPayment}
      />
    </div>
  );
};

export default InvQuote;
